/**
 * Helpers for values produced by NotionEditor, which stores its content as a
 * BlockNote JSON document. Anything that renders such a value outside the
 * editor has to convert it first, otherwise the raw JSON is shown.
 */

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Link targets a note may point at; anything else (javascript:, data:, …) is dropped. */
const SAFE_LINK = /^(https?:|mailto:)/i;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function inlineContentToHtml(content: any[]): string {
    if (!Array.isArray(content)) return '';
    return content.map(inline => {
        if (inline.type === 'link') {
            const inner = inlineContentToHtml(inline.content);
            const href = typeof inline.href === 'string' ? inline.href.trim() : '';
            if (!SAFE_LINK.test(href)) return inner;
            return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
        }
        let text: string = escapeHtml(String(inline.text ?? ''));
        if (inline.styles?.bold) text = `<strong>${text}</strong>`;
        if (inline.styles?.italic) text = `<em>${text}</em>`;
        if (inline.styles?.underline) text = `<u>${text}</u>`;
        if (inline.styles?.strike) text = `<s>${text}</s>`;
        return text;
    }).join('');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function blocksToHtml(blocks: any[]): string {
    const result: string[] = [];
    let i = 0;
    while (i < blocks.length) {
        const block = blocks[i];
        if (block.type === 'bulletListItem') {
            const items: string[] = [];
            while (i < blocks.length && blocks[i].type === 'bulletListItem') {
                items.push(`<li>${inlineContentToHtml(blocks[i].content)}</li>`);
                i++;
            }
            result.push(`<ul>${items.join('')}</ul>`);
        } else if (block.type === 'numberedListItem') {
            const items: string[] = [];
            while (i < blocks.length && blocks[i].type === 'numberedListItem') {
                items.push(`<li>${inlineContentToHtml(blocks[i].content)}</li>`);
                i++;
            }
            result.push(`<ol>${items.join('')}</ol>`);
        } else {
            const content = inlineContentToHtml(block.content ?? []);
            if (block.type === 'heading') {
                const stored = Number(block.props?.level);
                const level = Number.isInteger(stored) && stored >= 1 && stored <= 6 ? stored : 2;
                result.push(`<h${level}>${content}</h${level}>`);
            } else {
                result.push(content ? `<p>${content}</p>` : '<p></p>');
            }
            i++;
        }
    }
    return result.join('');
}

/**
 * Convert a BlockNote JSON document to HTML. Values that are already HTML or
 * plain text are returned untouched, so this is safe to call on legacy content.
 * Text and links of a BlockNote document are escaped, but legacy values pass
 * through as they are: sanitize the result (DOMPurify) before rendering it.
 */
export function blockNoteToHtml(value: string): string {
    if (!value) return '';
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return blocksToHtml(parsed);
    } catch {
        // not JSON — treat as HTML
    }
    return value;
}

/** Convert a BlockNote JSON document, HTML, or plain text to a plain-text preview. */
export function blockNoteToPlainText(value: string): string {
    if (!value) return '';
    return blockNoteToHtml(value)
        .replace(/<\/(p|h[1-6]|li)>/gi, ' ')
        .replace(/<[^>]*>?/gm, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/\s{2,}/g, ' ')
        .trim();
}
